//react - windowed - select;

// AssetDropdown.jsx
import React, { useState, useMemo, useEffect } from "react";
import WindowedSelect from "react-windowed-select";
import styled from "styled-components";
import { createFilter, components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { selectStylesNew, formStyles } from "../constants/styles";
import {
  colorAdditional,
  colorBlue,
  colorGray,
  colorGreenHover,
  colorLightBlue,
  colorLightGray,
  colorLightRed,
  colorRed,
  colorTextNew,
} from "../../utils/palette";
import { colorStatusActive, colorStatusInactive } from "../../utils/palette";
import { Desc } from "./form/FormElements";
import { useTranslation } from "react-i18next";
import { setAssetType } from "../../features/filter/filterSlice";
import { CustomLoader } from "./TableElements";

// Radio-style selection: only one filter can be selected at a time
// "all_assets" -> asset_type 2, "vehicle" -> asset_type 0, "small_equipment" -> asset_type 1
const getAssetTypeFromSelectedFilter = (selectedFilter) => {
  switch (selectedFilter) {
    case "all_assets":
      return 2;
    case "vehicle":
      return 0;
    case "small_equipment":
      return 1;
    default:
      return 2; // Default to "all_assets"
  }
};

const getSelectedFilterFromAssetType = (assetTypeValue, supportsEquipment) => {
  if (!supportsEquipment) {
    // If equipment not supported, only "vehicle" is available
    return "vehicle";
  }
  switch (assetTypeValue) {
    case 0:
      return "vehicle";
    case 1:
      return "small_equipment";
    case 2:
      return "all_assets";
    default:
      return "all_assets"; // Default to "all_assets"
  }
};

/* ---------- component ---------- */
const AssetDropdown = ({
  vehicle,
  onChange,
  assets,
  isEquipmentSupported = false,
  isForm = false, // If true, allow full width expansion in forms; if false, limit width in listing screens
  isDisabled = false,
  placeholder,
  hideFilters = false,
  inputId,
  classNamePrefix,
}) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();

  // Access vehicles list from Redux state as fallback for equipment check
  const vehiclesList = useSelector((state) => state.vehicles.list);
  // Determine if small equipment is supported for this screen
  // const isSmallEquipmentSupported = useMemo(() => {
  //   if (!screenName) return true; // Default to true if no screenName provided
  //   const supportedScreens = ["issues", "service", "maintenancereminders"];
  //   return supportedScreens.includes(screenName.toLowerCase());
  // }, [screenName]);

  const assetType = useSelector(
    (state) => state.filter?.data?.filter_asset_type ?? 2,
  );

  // Radio-style selection: track which filter is selected
  // "all_assets" | "vehicle" | "small_equipment"
  const [selectedFilter, setSelectedFilter] = useState(() => {
    return isEquipmentSupported ? "all_assets" : "vehicle";
  });

  // control menu open state
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  // Loading state to hide UI glitch when switching filters
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Radio-style selection: select the clicked filter (only one can be selected)
  const selectFilter = (filterType) => {
    // Don't allow deselecting - always select the clicked filter
    // Show loader when filter changes
    if (selectedFilter === filterType) {
      return;
    }
    if (filterType !== selectedFilter && !isForm) {
      setIsFilterLoading(true);
    }
    setSelectedFilter(filterType);
    setMenuIsOpen(true);

    if (!isForm) {
      const nextAssetType = getAssetTypeFromSelectedFilter(filterType);
      dispatch(setAssetType(nextAssetType));

      // Set "All Vehicles" or "All Equipment" as selected value when filter changes
      if (filterType === "vehicle") {
        const allVehiclesOption = {
          veh_id: "",
          name: t("vehicles"),
          value: "all_vehicles",
          label: t("vehicles"),
          asset_type: "all_vehicles",
          isAllOption: true,
        };
        onChange(allVehiclesOption);
      } else if (filterType === "small_equipment" && isEquipmentSupported) {
        const allEquipmentOption = {
          veh_id: "",
          name: t("equipment"),
          value: "all_equipment",
          label: t("equipment"),
          asset_type: "all_equipment",
          isAllOption: true,
        };
        onChange(allEquipmentOption);
      } else if (filterType === "all_assets") {
        // Clear selection when "All Assets" is selected
        onChange(null);
      } else if (vehicle && onChange) {
        onChange(null);
      }
    }
  };

  useEffect(() => {
    if (!isForm) {
      setSelectedFilter(
        getSelectedFilterFromAssetType(assetType, isEquipmentSupported),
      );
      setTimeout(() => {
        setIsFilterLoading(false);
      }, 300);
    }
  }, [assetType, isEquipmentSupported, isForm]);

  // This handler runs in the capture phase BEFORE react-select's native handlers,
  // allowing us to prevent the menu-close behavior.
  const handleMouseDownCapture = (e, filterType) => {
    e.preventDefault(); // prevents focus shift / native default
    e.stopPropagation(); // stops react-select from catching this event
    selectFilter(filterType);
  };

  // keyboard support: select with Space/Enter
  const handleKeyDown = (e, filterType) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      selectFilter(filterType);
    }
  };

  // Helper function to get status based on veh_active and secondary_status
  const getStatus = (veh_active, secondary_status) => {
    // veh_active-0 or veh_active-2 -> Inactive
    if (veh_active === 0 || veh_active === 2) {
      return {
        text: t("inactive"),
        bg: colorStatusInactive,
        textColor: colorGray,
      };
    }
    // veh_active-1 and secondary_status-0 -> Active
    if (veh_active === 1 && secondary_status === 0) {
      return {
        text: t("active"),
        bg: "#DCFCE7",
        textColor: "#008236",
      };
    }
    // veh_active-1 and secondary_status-1 -> Out of Service
    if (veh_active === 1 && secondary_status === 1) {
      return {
        text: t("out_of_service"),
        bg: "#FFE2E2",
        textColor: "#C10007",
      }; // red color
    }
    return {
      text: t("inactive"),
      bg: "#F6F3F4",
      textColor: "#364153",
    }; // default
  };

  // Check if at least one equipment exists in the assets list
  const hasEquipment = useMemo(() => {
    return vehiclesList.some((a) => a.asset_type === 1);
  }, [vehiclesList]);

  // compute filtered options - include full asset data for custom rendering
  const filteredOptions = useMemo(() => {
    return assets.filter((a) => {
      if (selectedFilter === "all_assets") {
        // Show both vehicles and equipment
        return (
          a.asset_type === 0 || (a.asset_type === 1 && isEquipmentSupported)
        );
      } else if (selectedFilter === "vehicle") {
        // Show only vehicles
        return a.asset_type === 0;
      } else if (selectedFilter === "small_equipment") {
        // Show only equipment
        return a.asset_type === 1 && isEquipmentSupported;
      }
      return false;
    });
  }, [assets, selectedFilter, isEquipmentSupported]);

  // Clear loading state after selectedFilter changes and filteredOptions updates
  // useEffect(() => {
  //   if (isFilterLoading) {
  //     // Use a small delay to ensure the DOM has updated
  //     const timer = setTimeout(() => {
  //       setIsFilterLoading(false);
  //     }, 100);
  //     return () => clearTimeout(timer);
  //   }
  // }, [selectedFilter, isFilterLoading]);

  // Optimized filter function for better performance with large datasets
  const optimizedFilter = useMemo(
    () =>
      createFilter({
        ignoreAccents: false, // Prevents expensive diacritics stripping
        //matchFrom: "start", // Start matching from beginning for better performance
        trim: true,
        stringify: (option) => option.label,
      }),
    [],
  );

  // Custom Menu: keep innerProps untouched but put our filter header above children
  const CustomMenu = (props) => {
    const { children, innerRef, innerProps } = props;
    const { t } = useTranslation("common");
    return (
      <MenuContainer ref={innerRef} {...innerProps}>
        {/* NOTE: onMouseDownCapture on the buttons is the key fix */}
        {/* Only show filters if equipment exists */}
        {hasEquipment && !hideFilters && (
          <div>
            <FilterHeader>
              {/* "All Assets" button - only shown when equipment is supported */}
              {isEquipmentSupported && (
                <FilterButton
                  type="button"
                  tabIndex={-1}
                  active={selectedFilter === "all_assets"}
                  onMouseDownCapture={(e) =>
                    handleMouseDownCapture(e, "all_assets")
                  }
                  onKeyDown={(e) => handleKeyDown(e, "all_assets")}
                  aria-pressed={selectedFilter === "all_assets"}
                  aria-label="Select all assets"
                >
                  {t("all_assets")}
                </FilterButton>
              )}

              <FilterButton
                type="button"
                tabIndex={-1}
                active={selectedFilter === "vehicle"}
                onMouseDownCapture={(e) => handleMouseDownCapture(e, "vehicle")}
                onKeyDown={(e) => handleKeyDown(e, "vehicle")}
                aria-pressed={selectedFilter === "vehicle"}
                aria-label="Select vehicles"
              >
                {t("vehicles")}
              </FilterButton>

              {isEquipmentSupported && (
                <FilterButton
                  type="button"
                  tabIndex={-1}
                  active={selectedFilter === "small_equipment"}
                  onMouseDownCapture={(e) =>
                    handleMouseDownCapture(e, "small_equipment")
                  }
                  onKeyDown={(e) => handleKeyDown(e, "small_equipment")}
                  aria-pressed={selectedFilter === "small_equipment"}
                  aria-label="Select small equipment"
                >
                  {t("small_equipment")}
                </FilterButton>
              )}
            </FilterHeader>
            {!isEquipmentSupported && (
              <Desc className="px-1 py-2">
                {t("small_equipment_not_supported")}
              </Desc>
            )}
          </div>
        )}

        <OptionsContainer>
          {isFilterLoading && (
            <LoaderOverlay>
              <CustomLoader />
            </LoaderOverlay>
          )}
          {children}
        </OptionsContainer>
      </MenuContainer>
    );
  };

  const Option = ({ children, data, ...props }) => {
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
    const newProps = Object.assign(props, { innerProps: rest });

    const { name, license_no, veh_type, veh_active, secondary_status } = data;

    // Get status information
    const status = getStatus(veh_active, secondary_status);

    let optionDetails = `${license_no} • ${veh_type}`;
    if (!license_no) {
      optionDetails = veh_type;
    }

    return (
      <OptionItem>
        <components.Option {...newProps}>
          <OptionContent>
            <OptionLeft>
              <OptionName>{name || children}</OptionName>
              <Desc>{optionDetails}</Desc>
            </OptionLeft>
            <OptionRight>
              <StatusContainer background={status.bg}>
                <p
                  style={{
                    color: status.textColor,
                    fontSize: "12px",
                    fontWeight: "400",
                  }}
                >
                  {status.text}
                </p>
              </StatusContainer>
            </OptionRight>
          </OptionContent>
        </components.Option>
      </OptionItem>
    );
  };

  // Create styles function that uses the prop
  // Use formStyles when isForm is true, otherwise use assetStyles
  const getAssetStyles = useMemo(() => {
    const styleSource = isForm ? formStyles : assetStyles;

    return {
      ...styleSource,
      control: (baseStyles, state) => {
        const baseControlStyles = styleSource.control(baseStyles, state);
        // Control width should never expand - keep it fixed
        const controlWidth = isForm ? "100%" : "160px";
        // Height should stay fixed - no expansion on open
        const height = isForm ? "36px" : "32px";
        const minHeight = isForm ? "36px" : "32px";

        return {
          ...baseControlStyles,
          width: controlWidth,
          height: height,
          minHeight: minHeight,
        };
      },
      indicatorsContainer: (provided, state) => {
        const baseIndicatorsStyles = styleSource.indicatorsContainer(
          provided,
          state,
        );
        // Height should stay fixed - no expansion on open
        const height = isForm ? "36px" : "32px";
        return {
          ...baseIndicatorsStyles,
          height: height,
        };
      },
      container: (baseStyles, state) => {
        const baseContainerStyles = styleSource.container(baseStyles, state);
        // Container width should never expand - keep it fixed
        const containerWidth = isForm ? "100%" : "160px";
        return {
          ...baseContainerStyles,
          position: state.menuIsOpen ? "absolute" : "relative",
          left: state.menuIsOpen ? "0" : "auto",
          top: state.menuIsOpen ? "0" : "auto",
          width: containerWidth,
          zIndex: state.menuIsOpen ? 1001 : baseContainerStyles.zIndex,
        };
      },
      // menu: (provided, state) => {
      //   const baseMenuStyles = styleSource.menu(provided, state);
      //   // Higher z-index for modals (React Bootstrap modals use 1050, backdrop uses 1040)
      //   const menuZIndex = isForm ? 1060 : 1000;
      //   return {
      //     ...baseMenuStyles,
      //     zIndex: menuZIndex,
      //   };
      // },
      menuPortal: (base) => ({
        ...base,
        zIndex: isForm ? 1060 : 1000,
      }),
    };
  }, [isForm]);

  // Handle vehicle selection and close menu
  const handleChange = (selectedOption) => {
    // If clearing "All Vehicles" or "All Equipment", reset filter to "All Assets"
    if (
      !selectedOption &&
      !isForm &&
      (selectedFilter === "vehicle" || selectedFilter === "small_equipment") &&
      isEquipmentSupported
    ) {
      setSelectedFilter("all_assets");
      dispatch(setAssetType(2)); // 2 = all assets
    }

    onChange(selectedOption);
    setMenuIsOpen(false);
  };

  return (
    <Wrapper $isOpen={menuIsOpen} $isForm={isForm}>
      <WindowedSelect
        inputId={inputId}
        classNamePrefix={classNamePrefix}
        value={vehicle}
        onChange={handleChange}
        placeholder={placeholder || t("search_vehicles")}
        options={filteredOptions}
        isSearchable
        isClearable={!isForm}
        closeMenuOnSelect={true}
        isDisabled={isDisabled}
        // control menu open state
        menuIsOpen={menuIsOpen}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        components={{
          Menu: CustomMenu,
          IndicatorSeparator: () => null,
          Option: Option,
        }}
        //menuPlacement="auto"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={getAssetStyles}
        // Performance optimizations for large datasets
        windowThreshold={30} // Start virtualization after 50 items
        //itemSize={65} // Height of each menu item in pixels (matches custom option layout)
        //maxMenuHeight={300} // Maximum height of the menu
        maxMenuHeight={300}
        filterOption={optimizedFilter} // Use optimized filter function
        // Additional performance props
        cacheOptions={false} // Don't cache options (we're using useMemo)
        defaultOptions={false} // Don't load all options by default
      />
    </Wrapper>
  );
};

export default AssetDropdown;

export const assetStyles = {
  control: (baseStyles, state) => {
    return {
      ...baseStyles,
      //minWidth: "160px",
      //maxWidth: "160px",
      width: "100%",
      padding: "0px",
      margin: "0px",
      height: "32px",
      cursor: "pointer",
      color: colorTextNew,
      borderColor: state.isFocused ? colorLightBlue : colorLightGray,
      minHeight: "32px",
      borderRadius: "5px",
      zIndex: state.menuIsOpen ? 1001 : 1,
      boxShadow: state.isFocused ? null : null,
      borderColor:
        state.getValue().length > 0 &&
        state.getValue()[0]?.value !== "" &&
        state.getValue()[0]?.value !== "all"
          ? colorBlue
          : colorLightGray,
      borderWidth:
        state.getValue().length > 0 &&
        state.getValue()[0]?.value !== "" &&
        state.getValue()[0]?.value !== "all"
          ? "2px"
          : "1px",
      position: "relative",
      transition:
        "width 0.2s ease, max-width 0.2s ease, height 0.2s ease, min-height 0.2s ease",
    };
  },
  option: (baseStyles, state) => ({
    ...baseStyles,
    color: state.isFocused ? colorTextNew : colorTextNew,
    backgroundColor: state.isSelected
      ? colorLightGray
      : baseStyles.backgroundColor,
  }),
  singleValue: (baseStyles, state) => ({
    ...baseStyles,
    paddingBottom: state.getValue().length > 0 ? "4px" : "0px",
    color: colorTextNew,
    zIndex: 1,
  }),
  placeholder: (baseStyles, state) => ({
    ...baseStyles,
    padding: "5px 2px",
    margin: 1,
    color: colorAdditional,
    fontSize: "12px",
    textWrap: "nowrap",
  }),
  container: (baseStyles, state) => {
    const type = state.innerProps.id;
    return {
      ...baseStyles,
      zIndex: state.menuIsOpen ? 1001 : 20,
    };
  },
  valueContainer: (provided, state) => ({
    ...provided,
    padding: "0 6px",
    margin: 0,
  }),

  input: (provided, state) => ({
    ...provided,
    padding: "0 2px",
    margin: 0,
  }),
  indicatorSeparator: (state) => ({
    display: "none",
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    height: "32px",
  }),
  multiValueContainer: (provided, state) => ({
    ...provided,
    display: "none",
  }),
  menu: (provided, state) => ({
    ...provided,
    margin: 0,
    padding: 0,
    position: "absolute",
    zIndex: 1000,
    backgroundColor: "white",
    //boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)",
    //border: "1px solid #d0d0d0",
    borderRadius: "4px",
  }),
  menuList: (provided, state) => ({
    ...provided,
    backgroundColor: "white",
    padding: 0,
    border: "1px solid #eee",
  }),
  dropdownIndicator: (provided, state) => ({
    //...provided,
    padding: "0 8px 4px 8px",
    //color: state.isFocused || state.hasValue ? colorGray : colorLightGray,
    color: colorGray,
  }),
  clearIndicator: (provided, state) => ({
    ...provided,
    padding: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: colorGray,
    cursor: "pointer",
    "& > svg": {
      top: "-1px",
      position: "relative",
    },
    // "&:hover": {
    //   color: colorRed,
    // },
  }),
};

/* ---------- styled components ---------- */
const Wrapper = styled.div`
  font-family: "Inter", sans-serif;
  position: relative;
  display: inline-block;
  width: ${({ $isForm }) => ($isForm ? "100%" : "160px")};
  //min-width: 160px;
  //max-width: 160px;
  height: 32px;
  flex-shrink: 0;
  overflow: visible;
  z-index: ${({ $isOpen }) => ($isOpen ? "1001" : "1")};
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 6px;
  display: block;
`;

const FilterHeader = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 8px;
  border-bottom: 1px solid #eee;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const FilterButton = styled.button`
  border: none;
  padding: 4px 8px;
  border-radius: 16px;
  cursor: pointer;
  background-color: ${({ active }) => (active ? "#0369a1" : "#e5e7eb")};
  color: ${({ active }) => (active ? "white" : "#333")};
  font-size: 0.75rem;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    background-color: ${({ active }) => (active ? "#0369a1" : "#d1d5db")};
  }
`;

const MenuContainer = styled.div`
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  //border: 1px solid #d0d0d0;
  border-radius: 4px;
  overflow: hidden;
  width: 350px !important;
  min-width: 350px !important;
`;

const OptionsContainer = styled.div`
  //max-height: 200px;
  overflow-y: auto;
  position: relative;
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

const OptionItem = styled.div`
  cursor: pointer;
  height: auto;
  &:hover {
    background-color: ${colorLightGray};
  }
`;

const OptionContent = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  width: 100%;
  padding: 4px 8px;
`;

const OptionLeft = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const OptionName = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${colorTextNew};
  margin-bottom: 2px;
`;

// const OptionDetails = styled.div`
//   font-size: 12px;
//   color: ${colorGray};
// `;

const OptionRight = styled.div`
  display: flex;
`;

const StatusContainer = styled.div`
  display: flex;
  justify-content: center;
  width: fit-content;
  padding: 2px 8px;
  font-size: 12px;
  background: ${(p) => p.background && p.background};
  border-radius: 12px;
`;
